import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { computeEffectivePermissions, createChecker } from '../permissions/engine';
import type { EffectivePermissions, PermissionString } from '../permissions/types';
import type { AuthAdapter, AuthContextValue, AuthSession } from './types';

const AuthContext = createContext<AuthContextValue | null>(null);

const DENY_ALL: Pick<AuthContextValue, 'can' | 'canAny' | 'canAll' | 'canGroups'> = {
  can: () => false,
  canAny: () => false,
  canAll: () => false,
  canGroups: () => false,
};

export interface AuthProviderProps {
  readonly adapter: AuthAdapter;
  /** Aggregated app catalog from the module registry — wildcards expand against exactly this. */
  readonly catalog: readonly PermissionString[];
  /**
   * When a restore finds no session, start the interactive flow at once instead of rendering a
   * signed-out shell.
   *
   * Right for this console: every route needs a session, so a signed-out state is a redirect
   * waiting to happen and showing a "sign in" button first is one click of theatre. Wrong for
   * Storybook and tests, which is why it is off by default rather than baked into the provider.
   */
  readonly autoSignIn?: boolean;
  readonly children: ReactNode;
}

/**
 * Runs the permission engine once per login/refresh (Addendum A.2). Everything downstream —
 * route guards, nav filtering, `<RequirePermission>`, `usePermissions()` — is a set lookup.
 */
export function AuthProvider({
  adapter,
  catalog,
  autoSignIn = false,
  children,
}: AuthProviderProps): ReactNode {
  const [session, setSession] = useState<AuthSession | null>(null);
  const [status, setStatus] = useState<AuthContextValue['status']>('loading');

  useEffect(() => {
    let cancelled = false;
    void adapter
      .restore()
      .then((restored) => {
        if (cancelled) return;
        if (restored === null && autoSignIn) {
          // Stay in `loading`: the document is about to be replaced by the issuer's page, and
          // flashing a signed-out console on the way there says something untrue.
          void adapter.signIn();
          return;
        }
        setSession(restored);
        setStatus(restored === null ? 'unauthenticated' : 'authenticated');
      })
      .catch(() => {
        if (!cancelled) setStatus('unauthenticated');
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, autoSignIn]);

  const permissions = useMemo<EffectivePermissions | null>(
    () => (session === null ? null : computeEffectivePermissions(session.grants, catalog)),
    [session, catalog],
  );

  const signIn = useCallback(
    async (hint?: string) => {
      const next = await adapter.signIn(hint);
      // Null means an interactive redirect has started (AuthAdapter.signIn): this document is
      // going away, so there is nothing to set and nothing to render differently.
      if (next === null) return;
      setSession(next);
      setStatus('authenticated');
    },
    [adapter],
  );

  const signOut = useCallback(async () => {
    await adapter.signOut();
    setSession(null);
    setStatus('unauthenticated');
  }, [adapter]);

  const value = useMemo<AuthContextValue>(() => {
    const checker = permissions === null ? DENY_ALL : createChecker(permissions.effective);
    return {
      status,
      user: session?.user ?? null,
      tenant: session?.tenant ?? null,
      permissions,
      signIn,
      signOut,
      ...checker,
    };
  }, [status, session, permissions, signIn, signOut]);

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthContextValue {
  const context = use(AuthContext);
  if (context === null) {
    throw new Error('useAuth must be used inside <AuthProvider>.');
  }
  return context;
}

export { AuthContext };
